import { useCallback, useEffect, useState } from 'react';
import { useRememberedState } from '@/lib/filter-memory/use-filter-memory';
import { Link, useNavigate } from 'react-router-dom';
import { ChevronIcon, PencilIcon, PlusIcon, SearchIcon, TrashIcon } from '@/components/catalog/icons';
import { ApiError, api } from '@/lib/api';
import { cx } from '@/lib/cx';
import { formatDateTime } from '@/lib/format';
import type { MarketingTemplate, MarketingTemplateSummary, Paginated } from '@/lib/types';
import { useAuth } from '@/lib/use-auth';
import {
  Badge,
  Button,
  Combobox,
  type ComboboxOption,
  ConfirmDialog,
  EmptyState,
  Field,
  Input,
  Panel,
  PanelFooter,
  PanelHeader,
  Skeleton,
  Table,
  TableWrap,
  TBody,
  TD,
  TH,
  THead,
  TR,
} from '@/components/ui';
import { Notice } from '@/components/ui/Notice';
import { ImportHtmlModal } from './ImportHtmlModal';
import { NewTemplateModal } from './NewTemplateModal';

/*
 * The Templates tab: a searchable, paged list over `GET /marketing/templates`.
 *
 * ⚠️ **The filters live in component state, not in the URL.** The page already
 * owns `?tab=`, and a second set of keys would have to be cleared on every tab
 * switch or it would narrow a sibling tab's list. The cost, stated: a filtered
 * view is not shareable and does not survive a reload.
 */

const PER_PAGE = 25;
const SKELETON_ROWS = 5;

const ARCHIVED_OPTIONS: ComboboxOption[] = [
  { value: 'false', label: 'Active only' },
  { value: 'true', label: 'Archived only' },
];
const ARCHIVED_ANY = 'Active and archived';

const TEMPLATE_IN_USE = 'MARKETING_TEMPLATE_IN_USE';

function errorText(caught: unknown, fallback: string): string {
  if (caught instanceof ApiError) return caught.formMessage ?? caught.message;
  return caught instanceof Error ? caught.message : fallback;
}

export function TemplateList() {
  const { ability } = useAuth();
  const navigate = useNavigate();
  const canCreate = ability.canCreate('marketing-templates');
  const canUpdate = ability.canUpdate('marketing-templates');
  const canDelete = ability.canDelete('marketing-templates');

  const [search, setSearch] = useRememberedState('templates-search', '');
  const [archived, setArchived] = useRememberedState<string>('templates-archived', 'false', (raw) =>
    raw === '' || raw === 'false' || raw === 'true' ? raw : undefined,
  );
  const [page, setPage] = useState(1);
  const [reloadKey, setReloadKey] = useState(0);

  const [result, setResult] = useState<Paginated<MarketingTemplateSummary> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [newOpen, setNewOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);

  const [busyId, setBusyId] = useState<number | null>(null);
  const [rowError, setRowError] = useState<string | null>(null);

  const [pendingDelete, setPendingDelete] = useState<MarketingTemplateSummary | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [deleteInUse, setDeleteInUse] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    const timer = setTimeout(() => {
      setLoading(true);
      api
        .listMarketingTemplates(
          {
            page,
            perPage: PER_PAGE,
            ...(search.trim() ? { search: search.trim() } : {}),
            ...(archived ? { archived: archived === 'true' } : {}),
          },
          controller.signal,
        )
        .then((data) => {
          setResult(data);
          setError(null);
        })
        .catch((caught: unknown) => {
          if (controller.signal.aborted) return;
          setError(errorText(caught, 'Templates could not be loaded.'));
        })
        .finally(() => {
          if (!controller.signal.aborted) setLoading(false);
        });
    }, 250);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [page, search, archived, reloadKey]);

  const reload = useCallback(() => setReloadKey((k) => k + 1), []);

  async function duplicate(template: MarketingTemplateSummary) {
    setBusyId(template.id);
    setRowError(null);
    try {
      const response = await api.duplicateMarketingTemplate(template.id);
      navigate(`/marketing/templates/${response.data.id}`);
    } catch (caught) {
      setRowError(errorText(caught, `${template.name} could not be duplicated.`));
    } finally {
      setBusyId(null);
    }
  }

  async function setArchivedFlag(template: MarketingTemplateSummary, next: boolean) {
    setBusyId(template.id);
    setRowError(null);
    try {
      await api.updateMarketingTemplate(template.id, { isArchived: next });
      reload();
    } catch (caught) {
      setRowError(errorText(caught, `${template.name} could not be ${next ? 'archived' : 'restored'}.`));
    } finally {
      setBusyId(null);
    }
  }

  async function confirmDelete() {
    if (!pendingDelete) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      if (deleteInUse) {
        await api.updateMarketingTemplate(pendingDelete.id, { isArchived: true });
      } else {
        await api.deleteMarketingTemplate(pendingDelete.id);
      }
      setPendingDelete(null);
      setDeleteInUse(false);
      reload();
    } catch (caught) {
      if (caught instanceof ApiError && caught.status === 409 && caught.code === TEMPLATE_IN_USE) {
        setDeleteInUse(true);
        setDeleteError(caught.message);
      } else {
        setDeleteError(errorText(caught, 'Could not reach the server. Nothing was deleted.'));
      }
    } finally {
      setDeleting(false);
    }
  }

  function openCreated(template: MarketingTemplate) {
    setNewOpen(false);
    setImportOpen(false);
    navigate(`/marketing/templates/${template.id}`);
  }

  const rows = result?.data ?? [];
  const total = result?.meta.total ?? 0;
  const totalPages = Math.max(result?.meta.totalPages ?? 1, 1);
  const initialLoad = loading && !result;
  const filtered = search.trim() !== '' || archived !== 'false';
  const countLabel = initialLoad ? 'Loading…' : `${total} ${total === 1 ? 'template' : 'templates'}`;

  return (
    <>
      <Panel className="min-w-0" aria-labelledby="marketing-templates-heading">
        <PanelHeader
          id="marketing-templates-heading"
          title="Templates"
          description={countLabel}
          actions={
            canCreate ? (
              <Button variant="primary" size="sm" leadingIcon={<PlusIcon />} onClick={() => setNewOpen(true)}>
                New template
              </Button>
            ) : undefined
          }
        />
        <div className="flex flex-wrap items-center gap-2 border-b border-border px-4 py-3">
          <div className="w-full min-w-0 sm:w-64">
            <Field label="Search templates" hideLabel>
              {({ id }) => (
                <Input
                  id={id}
                  type="search"
                  placeholder="Search templates…"
                  leadingIcon={<SearchIcon />}
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setPage(1);
                  }}
                />
              )}
            </Field>
          </div>
          <div className="w-full min-w-0 sm:w-48">
            <Field label="Filter by archive state" hideLabel>
              {({ id, 'aria-describedby': describedBy }) => (
                <Combobox
                  id={id}
                  value={archived}
                  onChange={(next) => {
                    setArchived(next);
                    setPage(1);
                  }}
                  options={ARCHIVED_OPTIONS}
                  emptyOptionLabel={ARCHIVED_ANY}
                  placeholder={ARCHIVED_ANY}
                  searchPlaceholder="Search…"
                  aria-describedby={describedBy}
                />
              )}
            </Field>
          </div>
        </div>

        <p role="status" aria-live="polite" className="sr-only">
          {countLabel}
        </p>

        {error && (
          <div className="border-b border-border px-4 py-3">
            <Notice tone="danger" role="alert" title={<>Couldn&rsquo;t load templates.</>}>
              <span className="min-w-0 [overflow-wrap:anywhere]">{error}</span>
            </Notice>
          </div>
        )}
        {rowError && (
          <div className="border-b border-border px-4 py-3">
            <Notice tone="danger" role="alert" className="[overflow-wrap:anywhere]">
              {rowError}
            </Notice>
          </div>
        )}

        <div
          aria-busy={loading || undefined}
          className={cx(
            'min-w-0 transition-opacity duration-150 ease-out motion-reduce:transition-none',
            loading && result && 'opacity-60',
          )}
        >
          {initialLoad ? (
            <TableWrap frozenHeader>
              <Table className="min-w-[860px]">
                <TemplatesHead />
                <TBody>
                  {Array.from({ length: SKELETON_ROWS }, (_, i) => (
                    <TR key={i}>
                      <TD>
                        <Skeleton className="h-3 w-48" />
                      </TD>
                      <TD>
                        <Skeleton className="h-3 w-40" />
                      </TD>
                      <TD>
                        <Skeleton className="h-4 w-14" />
                      </TD>
                      <TD>
                        <Skeleton className="h-3 w-8" />
                      </TD>
                      <TD>
                        <Skeleton className="h-3 w-28" />
                      </TD>
                      <TD numeric>
                        <Skeleton className="ml-auto h-6 w-24" />
                      </TD>
                    </TR>
                  ))}
                </TBody>
              </Table>
            </TableWrap>
          ) : rows.length === 0 ? (
            error ? (
              <EmptyState title="Nothing to show" description="Couldn’t load the list — see the message above." />
            ) : filtered ? (
              <EmptyState
                title="No templates match"
                description="Try a different search, or show archived templates too."
                action={
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => {
                      setSearch('');
                      setArchived('false');
                      setPage(1);
                    }}
                  >
                    Clear filters
                  </Button>
                }
              />
            ) : (
              <EmptyState
                title="No templates yet"
                description="A campaign needs a template to send."
                action={
                  canCreate ? (
                    <Button variant="primary" size="sm" leadingIcon={<PlusIcon />} onClick={() => setNewOpen(true)}>
                      New template
                    </Button>
                  ) : undefined
                }
              />
            )
          ) : (
            <TableWrap frozenHeader>
              <Table className="min-w-[860px]">
                <TemplatesHead />
                <TBody>
                  {rows.map((template) => (
                    <TR key={template.id} interactive>
                      <TD>
                        <div className="min-w-0">
                          <Link
                            to={`/marketing/templates/${template.id}`}
                            className="text-sm font-medium text-fg hover:underline [overflow-wrap:anywhere]"
                          >
                            {template.name}
                          </Link>
                          {template.isArchived && (
                            <Badge tone="neutral" className="ml-2">
                              Archived
                            </Badge>
                          )}
                          {template.description && (
                            <p className="mt-0.5 mb-0 text-xs text-fg-muted [overflow-wrap:anywhere]">
                              {template.description}
                            </p>
                          )}
                        </div>
                      </TD>
                      <TD className="text-fg-secondary">
                        {template.subject ? (
                          <span className="[overflow-wrap:anywhere]">{template.subject}</span>
                        ) : (
                          <span className="text-fg-subtle">—</span>
                        )}
                      </TD>
                      <TD>
                        <Badge tone="neutral" variant="outline">
                          {template.editorMode === 'html' ? 'HTML' : 'Visual'}
                        </Badge>
                      </TD>
                      <TD className="text-fg-secondary tabular">
                        {template.usedByCampaigns === 0 ? (
                          <span className="text-fg-subtle">None</span>
                        ) : (
                          `${template.usedByCampaigns} ${template.usedByCampaigns === 1 ? 'campaign' : 'campaigns'}`
                        )}
                      </TD>
                      <TD className="text-fg-secondary tabular">{formatDateTime(template.updatedAt)}</TD>
                      <TD numeric>
                        <div className="flex flex-wrap justify-end gap-1.5">
                          <Button
                            variant="ghost"
                            size="sm"
                            iconOnly
                            leadingIcon={<PencilIcon />}
                            aria-label={`${canUpdate ? 'Edit' : 'Open'} ${template.name}`}
                            onClick={() => navigate(`/marketing/templates/${template.id}`)}
                          />
                          {canCreate && (
                            <Button
                              variant="ghost"
                              size="sm"
                              loading={busyId === template.id}
                              onClick={() => void duplicate(template)}
                            >
                              Duplicate
                            </Button>
                          )}
                          {canUpdate && (
                            <Button
                              variant="ghost"
                              size="sm"
                              disabled={busyId === template.id}
                              onClick={() => void setArchivedFlag(template, !template.isArchived)}
                            >
                              {template.isArchived ? 'Unarchive' : 'Archive'}
                            </Button>
                          )}
                          {canDelete && (
                            <Button
                              variant="danger"
                              size="sm"
                              iconOnly
                              leadingIcon={<TrashIcon />}
                              aria-label={`Delete ${template.name}`}
                              onClick={() => {
                                setDeleteError(null);
                                setDeleteInUse(false);
                                setPendingDelete(template);
                              }}
                            />
                          )}
                        </div>
                      </TD>
                    </TR>
                  ))}
                </TBody>
              </Table>
            </TableWrap>
          )}
        </div>

        {result && total > 0 && (
          <PanelFooter>
            <p className="m-0 text-center text-xs text-fg-muted tabular sm:text-left">
              Page {page} of {totalPages}
            </p>
            <div className="flex items-center gap-2">
              <Button
                variant="secondary"
                size="sm"
                className="flex-1 sm:flex-none"
                leadingIcon={<ChevronIcon direction="left" />}
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                Previous
              </Button>
              <Button
                variant="secondary"
                size="sm"
                className="flex-1 sm:flex-none"
                trailingIcon={<ChevronIcon direction="right" />}
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
              >
                Next
              </Button>
            </div>
          </PanelFooter>
        )}
      </Panel>

      <NewTemplateModal
        open={newOpen}
        onClose={() => setNewOpen(false)}
        onBlank={() => {
          setNewOpen(false);
          navigate('/marketing/templates/new');
        }}
        onImport={() => {
          setNewOpen(false);
          setImportOpen(true);
        }}
        onDuplicated={openCreated}
      />
      <ImportHtmlModal open={importOpen} onClose={() => setImportOpen(false)} onImported={openCreated} />

      <ConfirmDialog
        open={pendingDelete !== null}
        onClose={() => {
          setPendingDelete(null);
          setDeleteError(null);
          setDeleteInUse(false);
        }}
        onConfirm={() => void confirmDelete()}
        tone={deleteInUse ? 'primary' : 'danger'}
        title={pendingDelete ? `Delete ${pendingDelete.name}?` : 'Delete template?'}
        description={
          deleteInUse
            ? 'This template is used by a campaign, so it cannot be deleted. Archiving keeps it on those campaigns and out of new ones.'
            : 'The template will be deleted and can’t be recovered.'
        }
        confirmLabel={deleteInUse ? 'Archive instead' : 'Delete template'}
        loading={deleting}
        error={deleteError && <span className="[overflow-wrap:anywhere]">{deleteError}</span>}
      />
    </>
  );
}

function TemplatesHead() {
  return (
    <THead>
      <TR>
        <TH>Name</TH>
        <TH className="w-64">Subject</TH>
        <TH className="w-24">Mode</TH>
        <TH className="w-32">Used by</TH>
        <TH className="w-44">Updated</TH>
        <TH className="w-64" numeric>
          Actions
        </TH>
      </TR>
    </THead>
  );
}
